import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import AppLayout from '../../components/layout/AppLayout'
import MobileOrderHistory from '../../components/purchase/mobile/MobileOrderHistory'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useReplenishment } from '../../hooks/useReplenishment'
import { deleteOrder, getOrderPdfUrl, updateOrderStatus, usePurchaseOrders } from '../../hooks/usePurchaseOrders'
import RupturaForecastCard from '../../components/purchase/RupturaForecastCard'
import { forecastRupturaElimination } from '../../lib/rupturaForecast'
import { generateOrderPdfBlob, generateCombinedOrderPdfBlob } from '../../lib/orderPdf'
import { buildGmailLink, buildMailtoLink, buildWhatsAppLink } from '../../lib/orderShare'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import { orderStatusColor as STATUS_BADGE } from '../../lib/statusStyles'
import {
  LEAD_TIME_BADGE,
  LEAD_TIME_LABEL,
  LEAD_TIME_TARGET,
  getLeadTimeStatus,
  leadTimeFulfillmentIndex,
} from '../../lib/leadTime'
import type { Company, OrderStatus, PurchaseOrderWithItems } from '../../types'

const STATUS_ORDER: OrderStatus[] = ['Pendente', 'Enviado', 'Recebido', 'Cancelado']

const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'

const leadInput = (o: PurchaseOrderWithItems) => ({
  status: o.status,
  estimated_arrival: o.estimated_arrival,
  received_at: o.received_at,
  sent_at: o.sent_at,
  lead_time_days: o.suppliers?.lead_time_days ?? null,
})

export default function OrderHistoryPage() {
  const isMobile = useIsMobile()
  const { orders, loading, refresh, checkReceipt } = usePurchaseOrders()
  const { rows } = useReplenishment()
  const [filter, setFilter] = useState<OrderStatus | 'all'>('all')
  const [supplierFilter, setSupplierFilter] = useState<string | 'all'>('all')
  const [selected, setSelected] = useState<PurchaseOrderWithItems | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [pdfLoading, setPdfLoading] = useState<string | null>(null)
  const [checking, setChecking] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [combinedLoading, setCombinedLoading] = useState(false)
  const { notify } = useToast()

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from('profiles').select('company_id').eq('id', user.id).single().then(({ data: profile }) => {
        if (!profile) return
        supabase.from('companies').select('*').eq('id', profile.company_id).single().then(({ data, error }) => {
          if (data) setCompany(data as Company)
          else notify('error', `Erro ao carregar empresa: ${error?.message ?? 'tente recarregar a página.'}`)
        })
      })
    })
  }, [])

  // Lista de fornecedores distintos entre os pedidos carregados — opções do filtro.
  const supplierOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of orders) if (o.supplier_id && o.suppliers?.name) map.set(o.supplier_id, o.suppliers.name)
    return [...map].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [orders])

  // Filtro de fornecedor aplicado antes do status — assim os contadores dos chips
  // de status refletem o fornecedor selecionado.
  const bySupplier = useMemo(
    () => supplierFilter === 'all' ? orders : orders.filter(o => o.supplier_id === supplierFilter),
    [orders, supplierFilter]
  )

  const filtered = useMemo(
    () => filter === 'all' ? bySupplier : bySupplier.filter(o => o.status === filter),
    [bySupplier, filter]
  )

  const counts = STATUS_ORDER.reduce<Record<OrderStatus, number>>((acc, s) => {
    acc[s] = bySupplier.filter(o => o.status === s).length
    return acc
  }, {} as Record<OrderStatus, number>)

  // Índice de atendimento ao lead time: % de pedidos recebidos na data prevista (meta 95%).
  const leadIndex = useMemo(() => leadTimeFulfillmentIndex(orders.map(leadInput)), [orders])

  // Previsão de ruptura: projeta o índice ao longo dos próximos dias, netando as
  // chegadas dos pedidos a caminho (Pendente/Enviado) contra a demanda prevista.
  const rupturaForecast = useMemo(
    () => forecastRupturaElimination({
      rows: rows.map(r => ({
        id: r.id,
        current_stock: r.current_stock,
        kanban_status: r.kanban_status,
        monthly_demand: r.demanda_prevista,
      })),
      orders,
    }),
    [rows, orders],
  )

  async function changeStatus(orderId: string, status: OrderStatus) {
    const err = await updateOrderStatus(orderId, status)
    if (err) notify('error', err)
    else refresh()
  }

  async function handleCheckReceipt(orderId: string) {
    setChecking(orderId)
    try {
      const res = await checkReceipt(orderId)
      if ('error' in res) notify('error', res.error)
      else if (res.received) notify('success', 'Pedido recebido — todos os itens entraram em estoque.')
      else notify('success', 'Ainda não recebido: nem todos os itens tiveram entrada de estoque.')
    } finally {
      setChecking(null)
    }
  }

  async function downloadPdf(order: PurchaseOrderWithItems) {
    setPdfLoading(order.id)
    try {
      // Caminho 1: URL assinada do Storage — navegação direta, sem popup, sem blob.
      // O browser mobile abre o PDF nativamente (viewer built-in).
      if (order.pdf_path) {
        const signedUrl = await getOrderPdfUrl(order.pdf_path)
        if (signedUrl) {
          window.location.href = signedUrl
          return
        }
      }

      // Caminho 2: gerar na hora via react-pdf e abrir como object URL
      if (!company) {
        notify('error', 'PDF não disponível. Recarregue a página e tente novamente.')
        return
      }
      const blob = await generateOrderPdfBlob({ order, supplier: order.suppliers, company })
      const objectUrl = URL.createObjectURL(blob)
      window.location.href = objectUrl
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (e) {
      notify('error', `Erro ao abrir PDF: ${e instanceof Error ? e.message : 'tente novamente.'}`)
    } finally {
      setPdfLoading(null)
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAllVisible() {
    setSelectedIds(prev => {
      const visibleIds = filtered.map(o => o.id)
      const allSelected = visibleIds.every(id => prev.has(id))
      const next = new Set(prev)
      if (allSelected) visibleIds.forEach(id => next.delete(id))
      else visibleIds.forEach(id => next.add(id))
      return next
    })
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  async function downloadCombinedPdf() {
    if (selectedIds.size === 0) return
    if (!company) {
      notify('error', 'Empresa não carregada, recarregue a página.')
      return
    }
    // Mantém a ordem da tabela (created_at desc)
    const selectedOrders = orders.filter(o => selectedIds.has(o.id))
    setCombinedLoading(true)
    try {
      const blob = await generateCombinedOrderPdfBlob(selectedOrders, company)
      // Download via âncora (não usa window.open, que o navegador bloqueia
      // como pop-up quando a geração demora — caso de muitos pedidos).
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pedidos-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      notify('error', `Erro ao gerar PDF: ${e instanceof Error ? e.message : 'tente novamente.'}`)
    } finally {
      setCombinedLoading(false)
    }
  }

  function sendEmail(order: PurchaseOrderWithItems) {
    if (!order.suppliers?.email) {
      notify('error', 'Cadastre o e-mail do fornecedor em Configurações.')
      return
    }
    if (!company) { notify('error', 'Empresa não carregada, recarregue a página.'); return }
    // mailto: abre o cliente de e-mail padrão do sistema — use Gmail se não tiver cliente configurado
    const link = buildMailtoLink({ order, supplier: order.suppliers, company, pdfSignedUrl: null })
    window.location.href = link
  }

  function sendGmail(order: PurchaseOrderWithItems) {
    if (!order.suppliers?.email) {
      notify('error', 'Cadastre o e-mail do fornecedor em Configurações.')
      return
    }
    if (!company) { notify('error', 'Empresa não carregada, recarregue a página.'); return }
    // Síncrono: abre Gmail direto sem await antes do window.open (evita bloqueio de popup)
    const link = buildGmailLink({ order, supplier: order.suppliers, company, pdfSignedUrl: null })
    const win = window.open(link, '_blank')
    if (!win) notify('error', 'Popup bloqueado. Permita popups para este site nas configurações do browser.')
  }

  async function removeOrder(order: PurchaseOrderWithItems) {
    if (!confirm(`Excluir definitivamente o pedido ${order.order_number}?`)) return
    const err = await deleteOrder(order.id, order.pdf_path ?? null)
    if (err) {
      notify('error', err)
      return
    }
    if (selected?.id === order.id) setSelected(null)
    notify('success', `Pedido ${order.order_number} excluído.`)
    refresh()
  }

  function sendWhatsApp(order: PurchaseOrderWithItems) {
    if (!order.suppliers?.phone) {
      notify('error', 'Cadastre o telefone do fornecedor em Configurações.')
      return
    }
    window.open(buildWhatsAppLink({ order, supplier: order.suppliers }), '_blank')
  }

  if (isMobile) {
    return (
      <AppLayout title="Histórico de Pedidos">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <MobileOrderHistory orders={orders} onSelect={setSelected} rupturaForecast={rupturaForecast} />
        )}
        {/* Modal de detalhe é o mesmo — já é responsivo (max-w-2xl + items-end no mobile) */}
        {selected && renderDetailModal()}
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Histórico de Pedidos">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === 'all' ? 'bg-brand-600/15 text-brand-700 dark:text-brand-300 border border-brand-600/40' : 'bg-sunken text-ink-muted border border-line'}`}
            >
              Todos · {bySupplier.length}
            </button>
            {STATUS_ORDER.map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
                  filter === s ? STATUS_BADGE[s] : 'bg-sunken text-ink-muted border border-line'
                }`}
              >
                <span>{s}</span>
                <span className="bg-black/20 dark:bg-white/20 rounded-full px-1.5 py-0.5 text-xs tabular-nums">{counts[s]}</span>
              </button>
            ))}
            <select
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              aria-label="Filtrar por fornecedor"
              className="sm:ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-sunken text-ink-muted border border-line cursor-pointer max-w-[220px]"
            >
              <option value="all">Todos os fornecedores</option>
              {supplierOptions.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Índice de atendimento ao lead time (meta 95% entregues na data) */}
          {(() => {
            const pct = leadIndex.index != null ? leadIndex.index * 100 : null
            const meta = LEAD_TIME_TARGET * 100
            const ok = pct != null && pct >= meta
            return (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 bg-surface border border-line rounded-xl px-4 py-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-ink-faint">Atendimento ao lead time</p>
                  <p className={`text-2xl font-bold tabular-nums leading-tight ${
                    pct == null ? 'text-ink-faint' : ok ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {pct != null ? `${pct.toFixed(0)}%` : '—'}
                    <span className="text-xs font-normal text-ink-faint ml-2">meta {meta.toFixed(0)}%</span>
                  </p>
                </div>
                <p className="text-xs text-ink-faint">
                  {leadIndex.evaluated > 0
                    ? `${leadIndex.onTime} de ${leadIndex.evaluated} pedido(s) recebido(s) entregues na data prevista`
                    : 'Sem pedidos recebidos com previsão para calcular'}
                </p>
              </div>
            )
          })()}

          {/* Previsão de fim da ruptura conforme os pedidos a caminho chegam */}
          <RupturaForecastCard forecast={rupturaForecast} />

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 bg-base-600/15 border border-base-600/40 rounded-lg px-4 py-2.5">
              <span className="text-sm text-ink font-medium">{selectedIds.size} selecionado(s)</span>
              <button
                onClick={downloadCombinedPdf}
                disabled={combinedLoading}
                className="text-xs px-3 py-1.5 bg-base-600 hover:bg-base-500 disabled:opacity-50 rounded-lg text-white font-medium"
              >
                {combinedLoading ? 'Gerando...' : `PDF dos selecionados (${selectedIds.size})`}
              </button>
              <button
                onClick={clearSelection}
                className="text-xs px-3 py-1.5 bg-sunken hover:bg-line rounded-lg text-ink-muted"
              >
                Limpar seleção
              </button>
            </div>
          )}

          <div className="bg-surface border border-line rounded-xl max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-sunken border-b border-line sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos os pedidos visíveis"
                      checked={filtered.length > 0 && filtered.every(o => selectedIds.has(o.id))}
                      onChange={toggleSelectAllVisible}
                      className="cursor-pointer accent-base-600"
                    />
                  </th>
                  {['Nº Pedido', 'Data', 'Fornecedor', 'Itens', 'Total', 'Status', 'Envio', 'Recebimento', 'Previsão', 'Atendimento', 'Ações'].map((c) => (
                    <th key={c} className="text-left text-xs font-semibold text-ink-muted uppercase px-3 py-3 whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-12 text-center text-ink-faint">Nenhum pedido encontrado.</td></tr>
                ) : filtered.map(o => (
                  <tr key={o.id} className="border-b border-line hover:bg-sunken cursor-pointer group"
                      onClick={() => setSelected(o)}>
                    <td className="px-3 py-2.5 w-10" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Selecionar pedido ${o.order_number}`}
                        checked={selectedIds.has(o.id)}
                        onChange={() => toggleSelect(o.id)}
                        className="cursor-pointer accent-base-600"
                      />
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-ink">{o.order_number}</td>
                    <td className="px-3 py-2.5 text-ink-muted whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    <td className="px-3 py-2.5 text-ink-muted max-w-[220px] truncate">{o.suppliers?.name ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink-muted">{o.purchase_order_items.length}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-medium text-ink">{fmtMoney(o.total_value)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${STATUS_BADGE[o.status]}`}>{o.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-ink-muted whitespace-nowrap">{fmtDate(o.sent_at)}</td>
                    <td className="px-3 py-2.5 text-ink-muted whitespace-nowrap">{fmtDate(o.received_at)}</td>
                    <td className="px-3 py-2.5 text-ink-muted whitespace-nowrap">{fmtDate(o.estimated_arrival)}</td>
                    <td className="px-3 py-2.5">
                      {(() => {
                        const st = getLeadTimeStatus(leadInput(o))
                        return <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${LEAD_TIME_BADGE[st]}`}>{LEAD_TIME_LABEL[st]}</span>
                      })()}
                    </td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => downloadPdf(o)} title="Baixar PDF" disabled={pdfLoading === o.id}
                                className="text-xs px-2 py-1 bg-sunken hover:bg-line disabled:opacity-50 rounded text-ink-muted">
                          {pdfLoading === o.id ? '...' : 'PDF'}
                        </button>
                        <button onClick={() => sendEmail(o)} title="Enviar via cliente de e-mail padrão"
                                className="text-xs px-2 py-1 bg-sunken hover:bg-line rounded text-ink-muted">E-mail</button>
                        <button onClick={() => sendGmail(o)} title="Enviar via Gmail"
                                className="text-xs px-2 py-1 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded text-danger">Gmail</button>
                        <button onClick={() => sendWhatsApp(o)} title="Enviar WhatsApp"
                                className="text-xs px-2 py-1 bg-sunken hover:bg-line rounded text-ink-muted">WApp</button>
                        {o.status === 'Pendente' && (
                          <button onClick={() => changeStatus(o.id, 'Enviado')} title="Marcar como Enviado"
                                  className="text-xs px-2 py-1 bg-sky-700 hover:bg-sky-600 rounded text-white">Enviado</button>
                        )}
                        {o.status === 'Enviado' && (
                          <>
                            <button onClick={() => handleCheckReceipt(o.id)} disabled={checking === o.id}
                                    title="Puxar saldo do Tiny ao vivo e verificar recebimento"
                                    className="text-xs px-2 py-1 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 rounded text-white">
                              {checking === o.id ? '...' : 'Verificar'}
                            </button>
                            <button onClick={() => changeStatus(o.id, 'Recebido')} title="Marcar como Recebido"
                                    className="text-xs px-2 py-1 bg-brand-600 hover:bg-brand-700 rounded text-white">Recebido</button>
                          </>
                        )}
                        <button onClick={() => removeOrder(o)} title="Excluir pedido"
                                className="text-xs px-2 py-1 bg-sunken hover:bg-danger/10 rounded text-ink-muted hover:text-danger">×</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && renderDetailModal()}
    </AppLayout>
  )

  function renderDetailModal() {
    if (!selected) return null
    return createPortal(
      <div suppressHydrationWarning>
        <div
          className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
        >
          <div className="bg-surface border border-line rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90dvh] overflow-y-auto overscroll-contain"
               onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-sm border-b border-line mb-4">
              <div className="px-3 sm:px-6 pt-3 pb-2 flex items-center justify-between gap-2">
                <button
                  onClick={() => setSelected(null)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sunken active:bg-line text-ink text-sm font-medium touch-action-manip"
                  aria-label="Voltar"
                >
                  <span aria-hidden>←</span>
                  <span>Voltar</span>
                </button>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${STATUS_BADGE[selected.status]}`}>
                    {selected.status}
                  </span>
                  <button
                    onClick={() => setSelected(null)}
                    aria-label="Fechar"
                    className="text-ink-muted hover:text-ink text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-sunken active:bg-line"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="px-4 sm:px-6 pb-3 min-w-0">
                <p className="text-[10px] text-ink-faint uppercase tracking-wider">Pedido</p>
                <h2 className="text-lg sm:text-xl font-bold text-ink truncate">{selected.order_number}</h2>
                <p className="text-sm text-ink-muted mt-0.5 truncate">{selected.suppliers?.name ?? '—'}</p>
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-24 sm:pb-6">

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-xs">
              <div>
                <p className="text-ink-faint">Criado em</p>
                <p className="text-ink">{fmtDate(selected.created_at)}</p>
              </div>
              <div>
                <p className="text-ink-faint">Enviado em</p>
                <p className="text-ink">{fmtDate(selected.sent_at)}</p>
              </div>
              <div>
                <p className="text-ink-faint">Previsão</p>
                <p className="text-ink">{fmtDate(selected.estimated_arrival)}</p>
              </div>
              <div>
                <p className="text-ink-faint">Atendimento lead time</p>
                {(() => {
                  const st = getLeadTimeStatus(leadInput(selected))
                  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-semibold ${LEAD_TIME_BADGE[st]}`}>{LEAD_TIME_LABEL[st]}</span>
                })()}
              </div>
            </div>

            {/* Mobile: lista de cards (sem scroll horizontal) */}
            <div className="sm:hidden space-y-2 mb-4">
              {selected.purchase_order_items.map(it => (
                <div key={it.id} className="bg-sunken border border-line rounded-lg p-3">
                  <p className="text-ink font-medium text-sm leading-snug">{it.product_name}</p>
                  <p className="font-mono text-[11px] text-ink-muted mt-1 truncate">{it.sku}</p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-line text-xs">
                    <span className="text-ink-muted">Qtd <span className="text-ink font-semibold tabular-nums ml-1">{it.quantity}</span></span>
                    <span className="text-ink font-semibold tabular-nums">{fmtMoney(it.subtotal)}</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-1 pt-2 border-t border-line">
                <span className="text-ink-muted text-sm">Total</span>
                <span className="text-lg font-bold text-ink tabular-nums">{fmtMoney(selected.total_value)}</span>
              </div>
            </div>

            {/* sm+: tabela tradicional */}
            <div className="hidden sm:block overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead className="bg-sunken">
                <tr>
                  <th className="text-left text-xs px-3 py-2 text-ink-muted">SKU</th>
                  <th className="text-left text-xs px-3 py-2 text-ink-muted">Produto</th>
                  <th className="text-right text-xs px-3 py-2 text-ink-muted">Qtd</th>
                  <th className="text-right text-xs px-3 py-2 text-ink-muted">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {selected.purchase_order_items.map(it => (
                  <tr key={it.id} className="border-b border-line">
                    <td className="px-3 py-2 font-mono text-xs text-ink">{it.sku}</td>
                    <td className="px-3 py-2 text-ink">{it.product_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{it.quantity}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">{fmtMoney(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line">
                  <td colSpan={3} className="px-3 py-2 text-right text-ink-muted">Total</td>
                  <td className="px-3 py-2 text-right text-lg font-bold text-ink">{fmtMoney(selected.total_value)}</td>
                </tr>
              </tfoot>
            </table>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => downloadPdf(selected)} disabled={pdfLoading === selected.id}
                      className="px-3 py-2 bg-sunken hover:bg-line disabled:opacity-50 rounded-lg text-sm text-ink-muted">
                {pdfLoading === selected.id ? 'Gerando PDF...' : 'Baixar PDF'}
              </button>
              <button onClick={() => sendEmail(selected)} className="px-3 py-2 bg-sunken hover:bg-line rounded-lg text-sm text-ink-muted">E-mail (cliente)</button>
              <button onClick={() => sendGmail(selected)} className="px-3 py-2 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded-lg text-sm text-danger">Gmail</button>
              <button onClick={() => sendWhatsApp(selected)} className="px-3 py-2 bg-sunken hover:bg-line rounded-lg text-sm text-ink-muted">Enviar WhatsApp</button>
              <button onClick={() => removeOrder(selected)} className="px-3 py-2 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded-lg text-sm text-danger">Excluir</button>
              <div className="sm:ml-auto flex gap-2 flex-wrap">
                {selected.status === 'Pendente' && (
                  <>
                    <button onClick={() => changeStatus(selected.id, 'Enviado')}
                            className="px-3 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm text-white font-medium">
                      Marcar como Enviado
                    </button>
                    <button onClick={() => changeStatus(selected.id, 'Cancelado')}
                            className="px-3 py-2 bg-sunken hover:bg-line rounded-lg text-sm text-ink-muted">
                      Cancelar
                    </button>
                  </>
                )}
                {selected.status === 'Enviado' && (
                  <>
                    <button onClick={() => handleCheckReceipt(selected.id)} disabled={checking === selected.id}
                            className="px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg text-sm text-white font-medium">
                      {checking === selected.id ? 'Verificando...' : 'Verificar recebimento'}
                    </button>
                    <button onClick={() => changeStatus(selected.id, 'Recebido')}
                            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 rounded-lg text-sm text-white font-medium">
                      Marcar como Recebido
                    </button>
                  </>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    )
  }
}
